/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import {bannerAutoplayHelper} from './banner-autoplay.helper';
import {bannerVideoWatchdog} from './banner-video-watchdog.helper';
import {videoDirective} from '../directives/video.directive';
import {videoPilot} from '../models/video/video-pilot';

/**
 * @typedef {import('swiper').Swiper} Swiper
 * @typedef {import('../models/video/abstract-video').AbstractVideo} AbstractVideo
 *
 * Banner carousel controller.
 *
 * Owns hero banner orchestration (thumbs, progress overlay, video slide sync, corner toggle).
 * Swiper `banner` preset and `swiper_bridge` video preset stay thin and delegate here.
 */

const mediaIcons = require('honda_cars_ecux/icons/media-controls');

const THUMB_PLAY_ICON = `<span class="thumb-play-icon" aria-hidden="true">${mediaIcons.play}</span>`;
const AUTOPLAY_TOGGLE_ICONS = [
  `<span class="swiper-autoplay-toggle__icon swiper-autoplay-toggle__icon--pause" aria-hidden="true">${mediaIcons.pause}</span>`,
  `<span class="swiper-autoplay-toggle__icon swiper-autoplay-toggle__icon--play" aria-hidden="true">${mediaIcons.play}</span>`
].join('');

/**
 * @param {Swiper} swiper
 *
 * @returns {object}
 */
function ensureBannerState(swiper) {
  if (!swiper.banner) {
    swiper.banner = {
      userPaused: false,
      wasOnVideo: false,
      lastProgressIndex: -1,
      playToken: 0,
      escapeTimer: null,
      syncTimer: null,
      playing: new Set()
    };
  }

  return swiper.banner;
}

/**
 * @param {Swiper}  swiper
 * @param {boolean} isPlaying
 */
function syncAutoplayToggle(swiper, isPlaying) {
  const host = swiper.el.closest('.banner--carousel') || swiper.el;
  const button = host.querySelector('.swiper-autoplay-toggle');

  if (!button) {
    return;
  }

  button.classList.toggle('is-playing', isPlaying);
  button.classList.toggle('is-paused', !isPlaying);
  button.setAttribute('aria-label', isPlaying ? 'Pause autoplay' : 'Play autoplay');
}

/**
 * Hide the carousel autoplay toggle on video slides — it only controls Swiper autoplay,
 * which is already stopped while a video owns the slide.
 *
 * @param {Swiper} swiper
 */
function syncAutoplayToggleVisibility(swiper) {
  const host = swiper.el.closest('.banner--carousel') || swiper.el;
  const container = host.querySelector('.swiper-autoplay-toggle-container');
  const button = host.querySelector('.swiper-autoplay-toggle');
  const hide = realIndexHasVideo(swiper);

  if (container) {
    container.classList.toggle('is-video-slide-active', hide);
    container.setAttribute('aria-hidden', hide ? 'true' : 'false');
  }

  if (button) {
    if (hide) {
      button.setAttribute('tabindex', '-1');
      button.setAttribute('aria-hidden', 'true');
    } else {
      button.removeAttribute('tabindex');
      button.removeAttribute('aria-hidden');
    }
  }
}

/**
 * @param {Swiper} swiper
 */
function initManualAutoPlayToggle(swiper) {
  const autoplayParams = swiper.params?.autoplay;

  if (!autoplayParams?.manualAutoPlay || autoplayParams.enabled === false) {
    return;
  }

  const host = swiper.el.closest('.banner--carousel') || swiper.el;

  if (host.querySelector('.swiper-autoplay-toggle')) {
    return;
  }

  const state = ensureBannerState(swiper);
  const container = document.createElement('div');
  const button = document.createElement('button');
  container.className = 'swiper-autoplay-toggle-container container';
  button.type = 'button';
  button.className = 'swiper-autoplay-toggle is-playing';
  button.setAttribute('aria-label', 'Pause autoplay');
  button.innerHTML = AUTOPLAY_TOGGLE_ICONS;

  state.userPaused = false;

  button.addEventListener('click', () => {
    // Flip first — resume refuses to run while userPaused is still set.
    state.userPaused = !state.userPaused;

    if (state.userPaused) {
      bannerAutoplayHelper.pause(swiper);
      syncAutoplayToggle(swiper, false);
    } else {
      bannerAutoplayHelper.resume(swiper);
      syncAutoplayToggle(swiper, true);
    }
  });

  container.appendChild(button);
  host.appendChild(container);
}

/**
 * @param {Swiper} swiper
 *
 * @returns {Swiper|null}
 */
function resolveThumbSwiper(swiper) {
  if (swiper.thumbs?.swiper) {
    return swiper.thumbs.swiper;
  }

  const host = swiper.el.closest('.banner--carousel');
  const thumbEl = host?.querySelector(
    '.thumbnails--carousel [amb-swiper], .thumbnails--carousel .swiper, #banner-thumbnails'
  );

  return thumbEl?.swiper || null;
}

/**
 * Banner wraps with `rewind`, so `realIndex` is the DOM position.
 *
 * @param {Swiper} swiper
 * @param {number} realIndex
 *
 * @returns {HTMLElement|null}
 */
function getSlideByRealIndex(swiper, realIndex) {
  return swiper.slides?.[realIndex] || null;
}

/**
 * @param {Swiper} swiper
 * @param {number} [realIndex]
 *
 * @returns {boolean}
 */
function realIndexHasVideo(swiper, realIndex = swiper.realIndex) {
  return !!getSlideByRealIndex(swiper, realIndex)?.querySelector('[amb-video]');
}

/**
 * @param {Swiper}      swiper
 * @param {HTMLElement} element
 *
 * @returns {number}
 */
function resolveSlideRealIndex(swiper, element) {
  const slide = element.matches?.('.swiper-slide') ? element : element.closest('.swiper-slide');

  return slide ? [...swiper.slides].indexOf(slide) : -1;
}

/**
 * @param {HTMLElement|null|undefined} slide
 *
 * @returns {HTMLElement|null}
 */
function resolveRegisteredVideoEl(slide) {
  const el = slide?.querySelector('[amb-video]');

  if (!el?.ambid) {
    return null;
  }

  const video = videoDirective.videos.get(el.ambid);

  return video && !video.isDestroyed() ? el : null;
}

/**
 * @param {Swiper} swiper
 * @param {number} realIndex
 *
 * @returns {HTMLElement|null}
 */
function findRegisteredVideoElForRealIndex(swiper, realIndex) {
  const registered = resolveRegisteredVideoEl(getSlideByRealIndex(swiper, realIndex));

  if (!registered) {
    return null;
  }

  return videoDirective.videos.get(registered.ambid)?.element || registered;
}

/**
 * @param {Swiper} swiper
 *
 * @returns {boolean}
 */
function initVideoThumbs(swiper) {
  const thumbSwiper = resolveThumbSwiper(swiper);

  if (!thumbSwiper) {
    return false;
  }

  thumbSwiper.slides.forEach((thumb) => {
    thumb.classList.remove('has-video');
    thumb.querySelector('.thumb-play-icon')?.remove();
  });

  swiper.slides.forEach((slide, realIndex) => {
    const thumb = thumbSwiper.slides[realIndex];

    if (!thumb || !slide.querySelector('[amb-video]')) {
      return;
    }

    thumb.classList.add('has-video');
    thumb.insertAdjacentHTML('beforeend', THUMB_PLAY_ICON);
  });

  return true;
}

/**
 * @param {Swiper} swiper
 * @param {number} [attempts]
 */
function ensureVideoThumbs(swiper, attempts = 10) {
  if (initVideoThumbs(swiper) || attempts <= 0) {
    return;
  }

  setTimeout(() => ensureVideoThumbs(swiper, attempts - 1), 50);
}

/**
 * @param {Swiper|null} thumbSwiper
 * @param {number}      realIndex
 * @param {object}      [options]
 * @param {boolean}     [options.slideTo=true]
 */
function syncThumbProgressTarget(thumbSwiper, realIndex, {slideTo = true} = {}) {
  thumbSwiper?.slides?.forEach((slide, index) => {
    slide.classList.toggle('is-banner-thumb-progress', index === realIndex);

    if (index !== realIndex && !slide.classList.contains('is-video-playing')) {
      slide.querySelector('.animation')?.style.removeProperty('translate');
    }
  });

  if (slideTo && typeof thumbSwiper?.slideTo === 'function' && thumbSwiper.activeIndex !== realIndex) {
    thumbSwiper.slideTo(realIndex);
  }
}

/**
 * @param {Swiper|null} thumbSwiper
 * @param {number}      realIndex
 */
function resetThumbProgress(thumbSwiper, realIndex) {
  const thumb = thumbSwiper?.slides?.[realIndex];

  if (!thumb || thumb.classList.contains('is-video-playing')) {
    return;
  }

  // Seed where autoplayTimeLeft starts (percentage 1 → -100%).
  thumb.querySelector('.animation')?.style.setProperty('translate', '-100% 0', 'important');
}

/**
 * @param {Swiper}        swiper
 * @param {AbstractVideo} video
 * @param {boolean}       isPlaying
 */
function setVideoThumbPlaying(swiper, video, isPlaying) {
  const thumbSwiper = resolveThumbSwiper(swiper);

  if (!thumbSwiper) {
    return;
  }

  const realIndex = resolveSlideRealIndex(swiper, video.element);

  if (realIndex < 0) {
    return;
  }

  thumbSwiper.slides.forEach((thumb, index) => {
    if (index !== realIndex) {
      thumb.classList.remove('is-video-playing');
      return;
    }

    thumb.classList.toggle('is-video-playing', isPlaying);

    if (isPlaying) {
      thumb.querySelector('.animation')?.style.setProperty('translate', '-100% 0');
    }
  });
}

/**
 * @param {Swiper} swiper
 */
function restartAutoplay(swiper) {
  const state = ensureBannerState(swiper);

  if (state.userPaused) {
    return;
  }

  state.playing.clear();
  bannerAutoplayHelper.start(swiper);

  const thumbSwiper = resolveThumbSwiper(swiper);

  syncThumbProgressTarget(thumbSwiper, swiper.realIndex);
  resetThumbProgress(thumbSwiper, swiper.realIndex);
}

/**
 * Start the active banner video via videoPilot (single rewind/play path).
 *
 * @param {Swiper}           swiper
 * @param {HTMLElement|null} element
 */
function playVideoElement(swiper, element) {
  if (!element || !swiper) {
    return;
  }

  const state = ensureBannerState(swiper);

  state.playToken += 1;

  const playToken = state.playToken;
  const video = element.ambid ? videoDirective.videos.get(element.ambid) : null;

  if (video && !video.isDestroyed()) {
    video.visible = true;
  }

  if (state.playToken !== playToken) {
    return;
  }

  videoPilot.enter(element, ['init', 'autoplay']);
}

/**
 * @param {Swiper} swiper
 */
function syncSlideVideos(swiper) {
  const state = ensureBannerState(swiper);
  const activeHasVideo = realIndexHasVideo(swiper);
  const thumbSwiper = resolveThumbSwiper(swiper);
  const activeRealIndex = swiper.realIndex;
  const wasOnVideoSlide = !!state.wasOnVideo;
  const enteredNewRealIndex = activeRealIndex !== state.lastProgressIndex;

  syncThumbProgressTarget(thumbSwiper, activeRealIndex);

  if (activeHasVideo) {
    if (!state.userPaused) {
      bannerAutoplayHelper.stop(swiper);
    }

    const activeThumb = thumbSwiper?.slides?.[activeRealIndex];

    if (activeThumb?.classList.contains('has-video')) {
      activeThumb.classList.add('is-video-playing');
      activeThumb.querySelector('.animation')?.style.setProperty('translate', '-100% 0');
    }

    state.lastProgressIndex = activeRealIndex;
  } else {
    thumbSwiper?.slides?.forEach((thumb) => {
      thumb.classList.remove('is-video-playing');

      if (thumb.classList.contains('has-video')) {
        thumb.querySelector('.animation')?.style.removeProperty('translate');
      }
    });

    if (enteredNewRealIndex || wasOnVideoSlide) {
      resetThumbProgress(thumbSwiper, activeRealIndex);
      state.lastProgressIndex = activeRealIndex;
    }

    // Wait for fade to settle — starting mid-transition shortens the dwell window.
    if (!swiper.autoplay?.running && !swiper.animating) {
      restartAutoplay(swiper);
    }
  }

  syncAutoplayToggle(swiper, !state.userPaused);
  syncAutoplayToggleVisibility(swiper);

  const activeVideoEl = activeHasVideo
    ? findRegisteredVideoElForRealIndex(swiper, activeRealIndex)
      || getSlideByRealIndex(swiper, activeRealIndex)?.querySelector('[amb-video]')
    : null;

  state.wasOnVideo = activeHasVideo;

  // Only leave when exiting a video slide — repeated leave() on image slides breaks Vimeo replay.
  if (wasOnVideoSlide && !activeHasVideo) {
    state.playToken += 1;

    swiper.slides.forEach((slide) => {
      const registered = resolveRegisteredVideoEl(slide);

      if (registered) {
        videoPilot.leave(registered, ['autopause']);
      }
    });
  }

  if (activeVideoEl) {
    // Fresh enter only — re-sync fires several times per change.
    if (!wasOnVideoSlide) {
      playVideoElement(swiper, activeVideoEl);
      bannerVideoWatchdog.arm(swiper);
    }
  } else {
    bannerVideoWatchdog.clear(swiper);
  }
}

/**
 * @param {Swiper} swiper
 */
function queueSlideSync(swiper) {
  const state = ensureBannerState(swiper);

  clearTimeout(state.syncTimer);

  state.syncTimer = setTimeout(() => {
    syncSlideVideos(swiper);
  }, 50);
}

/**
 * @param {Swiper} swiper
 */
function applyContentMotion(swiper) {
  const {slides, previousIndex, activeIndex} = swiper;

  slides.forEach((slide) => {
    slide.classList.remove('is-content-in', 'is-content-out');
  });

  if (slides[previousIndex] && previousIndex !== activeIndex) {
    slides[previousIndex].classList.add('is-content-out');
  }

  const activeSlide = slides[activeIndex];

  if (activeSlide) {
    // Restart enter keyframes when the class is re-applied
    // eslint-disable-next-line no-unused-expressions
    activeSlide.offsetWidth;
    activeSlide.classList.add('is-content-in');
  }
}

/**
 * @param {Swiper}        swiper
 * @param {AbstractVideo} video
 * @param {string|number} stateName
 */
function onVideoStateChange(swiper, video, stateName) {
  if (!swiper) {
    return;
  }

  const state = ensureBannerState(swiper);
  const uniqueKey = `${video.element.ambid}:${video.id}`;
  const playing = state.playing;

  /* global YT */
  const isEnded = stateName === 'ended' || stateName === YT?.PlayerState?.ENDED;
  const isPause = stateName === 'pause' || stateName === YT?.PlayerState?.PAUSED;

  switch (stateName) {
    case 'bufferstart':
    case 'play':
    case YT?.PlayerState?.BUFFERING:
    case YT?.PlayerState?.PLAYING:
      playing.add(uniqueKey);
      break;

    case 'pause':
    case 'ended':
    case YT?.PlayerState?.PAUSED:
    case YT?.PlayerState?.ENDED:
      playing.delete(uniqueKey);
      break;
  }

  if (playing.size === 0) {
    setVideoThumbPlaying(swiper, video, false);

    if (isEnded) {
      bannerVideoWatchdog.clear(swiper);
      swiper.slideNext();
      // Reset after queueing transition so sync still sees leaving a video slide.
      state.wasOnVideo = false;

      return;
    }

    const videoRealIndex = resolveSlideRealIndex(swiper, video.element);
    const videoOnActiveSlide = videoRealIndex >= 0 && videoRealIndex === swiper.realIndex;

    if (isPause && !videoOnActiveSlide) {
      return;
    }

    const activeHasVideo = !!swiper.slides?.[swiper.realIndex]?.querySelector('[amb-video]');

    if (activeHasVideo) {
      bannerAutoplayHelper.stop(swiper);
    } else {
      bannerAutoplayHelper.start(swiper);
    }
  } else {
    bannerAutoplayHelper.stop(swiper);
    setVideoThumbPlaying(swiper, video, true);
  }
}

export const bannerCarousel = {
  resolveThumbSwiper,

  /**
   * Swiper `on` handlers for the banner preset.
   *
   * @returns {object}
   */
  bindEvents() {
    return {
      /**
       * @param {Swiper} swiper
       */
      init(swiper) {
        ensureBannerState(swiper);
        swiper.slides[swiper.activeIndex]?.classList.add('is-content-in');
        initManualAutoPlayToggle(swiper);
        ensureVideoThumbs(swiper);
        setTimeout(() => {
          ensureVideoThumbs(swiper);
          syncSlideVideos(swiper);
        }, 0);
      },

      /**
       * @param {Swiper} swiper
       */
      destroy(swiper) {
        const state = swiper.banner;

        if (!state) {
          return;
        }

        clearTimeout(state.syncTimer);
        bannerVideoWatchdog.clear(swiper);
      },

      /**
       * @param {Swiper} swiper
       */
      slideChangeTransitionStart(swiper) {
        applyContentMotion(swiper);
        syncSlideVideos(swiper);
      },

      /**
       * @param {Swiper} swiper
       */
      slideChangeTransitionEnd(swiper) {
        if (!realIndexHasVideo(swiper) && !swiper.autoplay?.running) {
          restartAutoplay(swiper);
        }

        queueSlideSync(swiper);
      },

      /**
       * @param {Swiper} swiper
       */
      realIndexChange(swiper) {
        syncThumbProgressTarget(resolveThumbSwiper(swiper), swiper.realIndex);
        queueSlideSync(swiper);
      },

      /**
       * @param {Swiper} swiper
       */
      activeIndexChange(swiper) {
        syncThumbProgressTarget(resolveThumbSwiper(swiper), swiper.realIndex);
      },

      /**
       * Re-assert a user pause that Swiper lifted on transitionend / visibilitychange.
       *
       * Must `pause()` again — not `stop()`. `stop()` drops the banked remainder, so the next
       * toggle play falls through to `start()` and the thumb progress snaps back to 0.
       *
       * @param {Swiper} swiper
       */
      autoplayResume(swiper) {
        if (ensureBannerState(swiper).userPaused) {
          bannerAutoplayHelper.pause(swiper);
        }
      },

      /**
       * @param {Swiper} swiper
       * @param {number} timeLeft
       * @param {number} percentage
       */
      autoplayTimeLeft(swiper, timeLeft, percentage) {
        const state = ensureBannerState(swiper);
        const thumbSwiper = resolveThumbSwiper(swiper);

        if (!thumbSwiper || realIndexHasVideo(swiper) || state.userPaused) {
          return;
        }

        const realIndex = swiper.realIndex;

        syncThumbProgressTarget(thumbSwiper, realIndex, {slideTo: false});

        const animationEl = thumbSwiper.slides?.[realIndex]?.querySelector('.animation')
          || thumbSwiper.el.querySelector('.swiper-slide-thumb-active .animation')
          || thumbSwiper.el.querySelector('.is-banner-thumb-progress .animation');

        if (animationEl) {
          animationEl.style.setProperty(
            'translate',
            `-${(percentage * 100).toFixed(2)}% 0`,
            'important'
          );
        }
      }
    };
  },

  /**
   * Video event handlers for `swiper_bridge`.
   *
   * @param {Swiper} swiper
   *
   * @returns {object}
   */
  registerVideoEvents(swiper) {
    return {
      loop: false,
      onStateChange: (video, {data}) => onVideoStateChange(swiper, video, data),
      bufferstart: (video) => onVideoStateChange(swiper, video, 'bufferstart'),
      play: (video) => onVideoStateChange(swiper, video, 'play'),
      pause: (video) => onVideoStateChange(swiper, video, 'pause'),
      ended: (video) => onVideoStateChange(swiper, video, 'ended')
    };
  },

  onVideoStateChange
};
